/**
 * Contact Support Page - Trade Loop v2.0
 *
 * Contact support with a message form
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { api } from "@/lib/api";
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  MessageSquare,
  Send,
  Mail,
  Clock,
  CalendarDays,
  CheckCircle,
  Home
} from "lucide-react";

// Keep in sync with the "Response time" shown under "Other ways to reach us"
const RESPONSE_TIME = "within 24 hours";

const SUPPORT_CATEGORIES = [
  "General Question",
  "Technical Issue",
  "Account & Login",
  "Billing & Subscription",
  "Feedback & Suggestion",
  "Report a Problem",
] as const;

export default function ContactUsPage() {
  const { user, isAuthenticated } = useAuth();
  const { showError } = useToast();

  const [category, setCategory] = useState<string>(SUPPORT_CATEGORIES[0]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  // Guest contact details — only collected/shown when the visitor is not logged in
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  // Once a message is sent, we swap the form out for an on-page confirmation
  const [submittedInfo, setSubmittedInfo] = useState<{ name: string; email: string } | null>(null);

  // If user is not authenticated, render without AppLayout (no headers/footers)
  const isPublicView = !isAuthenticated;

  const sendMessageMutation = useMutation({
    mutationFn: async (data: { name: string; email: string; subject: string; message: string }) => {
      return api.post("/api/support/contact", data);
    },
    onSuccess: (_data, variables) => {
      // Capture the sender's details for the on-page confirmation before clearing the form
      setSubmittedInfo({ name: variables.name, email: variables.email });
      setCategory(SUPPORT_CATEGORIES[0]);
      setSubject("");
      setMessage("");
      setGuestName("");
      setGuestEmail("");
    },
    onError: (error: any) => {
      showError("Send Failed", error.message || "Failed to send message. Please try again.");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Logged-in users are identified by their profile; guests must supply name + email
    const name = isPublicView ? guestName.trim() : (user?.fullName || "User");
    const email = isPublicView ? guestEmail.trim() : (user?.email || "");

    if (isPublicView) {
      if (!name) {
        showError("Name Required", "Please enter your name");
        return;
      }

      if (!email) {
        showError("Email Required", "Please enter your email address");
        return;
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        showError("Invalid Email", "Please enter a valid email address");
        return;
      }
    }

    if (!subject.trim()) {
      showError("Subject Required", "Please enter a subject");
      return;
    }

    if (!message.trim()) {
      showError("Message Required", "Please enter your message");
      return;
    }

    if (message.length > 500) {
      showError("Message Too Long", "Message must be 500 characters or less");
      return;
    }

    sendMessageMutation.mutate({
      name,
      email,
      subject: `[${category}] ${subject.trim()}`,
      message: message.trim(),
    });
  };

  const pageContent = (
    <div className="max-w-2xl mx-auto p-4 pb-[41px] md:pb-4 space-y-6">
      {/* Back to Landing button for public users (hidden on the confirmation view, which has its own) */}
      {isPublicView && !submittedInfo && (
        <Link to="/">
          <Button variant="outline" className="flex items-center gap-2">
            <Send className="w-4 h-4 rotate-180" />
            Back to Home
          </Button>
        </Link>
      )}

      {submittedInfo ? (
        /* On-page confirmation shown after a successful submission */
        <Card>
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Thanks, {submittedInfo.name}!
            </h2>
            <p className="text-gray-600">
              Your message has been sent. Our support team will get back to you at{" "}
              <span className="font-medium text-gray-900">{submittedInfo.email}</span>{" "}
              {RESPONSE_TIME}.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                variant="outline"
                className="w-full sm:w-auto flex items-center justify-center gap-2"
                onClick={() => setSubmittedInfo(null)}
              >
                <Send className="w-4 h-4" />
                Send another message
              </Button>
              <Link to="/" className="w-full sm:w-auto">
                <Button className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white">
                  <Home className="w-4 h-4" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-8">
            {/* Icon and Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                We're here to help!
              </h2>
              <p className="text-gray-600">
                Have a question, issue or feedback? Send us a message and our support team will get back to you as soon as possible.
              </p>
              <p className="text-gray-600 mt-3 text-sm">
                Looking for a quick answer? Browse our{" "}
                <Link to="/faq" className="text-blue-600 hover:text-blue-700 font-medium">
                  Frequently Asked Questions
                </Link>
                .
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Send className="w-4 h-4 text-blue-600" />
                  Send us a message
                </h3>

                {/* Name & Email — only for logged-out visitors (logged-in users are identified automatically) */}
                {isPublicView && (
                  <>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="text"
                        placeholder="Your name"
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        required
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="email"
                        placeholder="you@example.com"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        We'll use this to reply to your request.
                      </p>
                    </div>
                  </>
                )}

                {/* Category */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="flex h-12 w-full rounded-md border border-input bg-white px-3 py-2 text-base text-gray-900 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm"
                    required
                  >
                    {SUPPORT_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Subject */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    placeholder="What can we help with?"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    required
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    placeholder="Please describe your issue or question in detail..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="min-h-[120px] resize-none"
                    maxLength={500}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {message.length}/500 characters
                  </p>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={
                    sendMessageMutation.isPending ||
                    !subject.trim() ||
                    !message.trim() ||
                    (isPublicView && (!guestName.trim() || !guestEmail.trim()))
                  }
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white"
                >
                  {sendMessageMutation.isPending ? (
                    "Sending..."
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </div>
            </form>

            {/* Other Ways to Reach Us */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4">
                Other ways to reach us
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="w-4 h-4 text-blue-600" />
                  <span className="font-medium">Email:</span>
                  <a
                    href="mailto:support@gotradeloop.com"
                    className="text-blue-600 hover:text-blue-700"
                  >
                    support@gotradeloop.com
                  </a>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="w-4 h-4 text-green-600" />
                  <span className="font-medium">Response time:</span>
                  <span>Within 24 hours</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <CalendarDays className="w-4 h-4 text-purple-600" />
                  <span className="font-medium">Support hours:</span>
                  <span>Monday - Friday, 9 AM - 5 PM EST</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      </div>
  );

  // For authenticated users, wrap in AppLayout (includes headers/footers)
  // For public users, render content directly (no headers/footers)
  return isPublicView ? (
    <div className="min-h-screen bg-gray-50 pt-5 md:pt-0">
      {pageContent}
    </div>
  ) : (
    <AppLayout activeTab="profile" title="Contact Support" backLink="/settings">
      {pageContent}
    </AppLayout>
  );
}
